// src/rms/components/ui.js
// ============================================================================
// Shared UI primitives for the RMS
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import { FIELD_TYPES } from "../verticals/presets";
import { optionLabelFor } from "../i18n/optionLabel";
import { getLocale } from "../i18n/locale";
import i18n from "../i18n";

/**
 * These primitives are used by screens inside RmsProvider AND by the public
 * pages outside it, so they cannot depend on either translator hook. The
 * i18next instance works in both places.
 */
const t = (key, fallback) => i18n.t(key, { defaultValue: fallback });

// ----------------------------------------------------------------------------
// Layout
// ----------------------------------------------------------------------------

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
      <div>
        <h1 className="rms-page-title">{title}</h1>
        {subtitle ? <p className="rms-page-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div> : null}
    </div>
  );
}

export function Stat({ label, value, hint }) {
  return (
    <div className="rms-stat">
      <div className="rms-stat-label">{label}</div>
      <div className="rms-stat-value">{value}</div>
      {hint ? <div className="rms-stat-hint">{hint}</div> : null}
    </div>
  );
}

export function Banner({ tone = "info", children }) {
  return <div className={`rms-banner rms-banner--${tone}`}>{children}</div>;
}

export function EmptyState({ title, message, action }) {
  return (
    <div className="rms-card rms-empty">
      <h3>{title}</h3>
      {message ? <p>{message}</p> : null}
      {action}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Status pills
// ----------------------------------------------------------------------------

const TONE_BY_STATUS = {
  available: "ok",
  returned: "ok",
  completed: "ok",
  paid: "ok",
  verified: "ok",
  signed: "ok",
  active: "info",
  confirmed: "info",
  reserved: "info",
  occupied: "info",
  sent: "info",
  rented: "warn",
  checked_out: "warn",
  pending: "warn",
  partially_paid: "warn",
  past_due: "warn",
  maintenance: "warn",
  notice_given: "warn",
  overdue: "danger",
  cancelled: "danger",
  no_show: "danger",
  declined: "danger",
  out_of_service: "danger",
  void: "danger",
  evicted: "danger",
  blocked: "danger",
};

export function StatusPill({ status, label }) {
  const tone = TONE_BY_STATUS[status] || "neutral";
  const text = label || String(status || "").replace(/_/g, " ");
  return <span className={`rms-pill rms-pill--${tone}`}>{text}</span>;
}

// ----------------------------------------------------------------------------
// Modal
// ----------------------------------------------------------------------------

/**
 * Marks a row as part of the worked example every operator is provisioned with.
 *
 * Without it a demo vehicle is indistinguishable from a real one, which is a
 * trap the first time someone books one out for a paying customer. Deliberately
 * quiet -- neutral, small, after the name -- because it is a caveat on the row,
 * not a status of the thing itself.
 */
export function DemoPill() {
  return (
    <span className="rms-pill rms-pill--demo" title={t("demo.hint", "Sample data, not one of your records. Remove it from Settings → Your data.")}>
      {t("demo.label", "Demo")}
    </span>
  );
}

export function Modal({ title, children, footer, onClose, width = 620 }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="rms-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="rms-modal" style={{ maxWidth: width }} role="dialog" aria-modal="true" aria-label={title}>
        <div className="rms-modal-head">
          <strong style={{ fontSize: 15 }}>{title}</strong>
          {/* Omitting onClose makes a modal that cannot be dismissed -- Escape
              and the backdrop already no-op without it, and rendering a ✕ that
              does nothing is worse than rendering none. Used where there is no
              sensible "not now": see RequirePin. */}
          {onClose ? (
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={onClose} aria-label={t("ui.close", "Close")}>
              ✕
            </button>
          ) : null}
        </div>
        <div className="rms-modal-body">{children}</div>
        {footer ? <div className="rms-modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Fields
// ----------------------------------------------------------------------------

export function Field({ label, required, help, children }) {
  return (
    <div className="rms-field">
      <label className="rms-label">
        {label}
        {required ? <span className="rms-required">*</span> : null}
      </label>
      {children}
      {help ? <div className="rms-help">{help}</div> : null}
    </div>
  );
}

export function TextInput({ value, onChange, ...rest }) {
  return <input className="rms-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

export function Select({ value, onChange, options, placeholder = "Select…", ...rest }) {
  return (
    <select className="rms-select" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest}>
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        return (
          <option key={val} value={val}>
            {optionLabelFor(o)}
          </option>
        );
      })}
    </select>
  );
}

/**
 * Renders one field from a vertical's assetSchema. This is the component that
 * makes a car's "VIN / fuel level" and a locker's "unit size / gate code" the
 * same code path — the schema is data, so the form is generated, never written
 * per vertical.
 */
export function DynamicField({ field, value, onChange, allValues = {} }) {
  // Conditional fields: battery charge only matters on an e-bike.
  if (field.showIf) {
    const target = allValues[field.showIf.field];
    if (target !== field.showIf.equals) return null;
  }

  const common = { required: field.required };

  switch (field.type) {
    case FIELD_TYPES.NUMBER:
      return (
        <Field label={labelWithUnit(field)} required={field.required} help={field.help}>
          <input
            className="rms-input"
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            {...common}
          />
        </Field>
      );

    case FIELD_TYPES.PERCENT:
      return (
        <Field label={`${field.label} (%)`} required={field.required} help={field.help}>
          <input
            className="rms-input"
            type="number"
            min="0"
            max="100"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            {...common}
          />
        </Field>
      );

    case FIELD_TYPES.SELECT:
      return (
        <Field label={field.label} required={field.required} help={field.help}>
          <Select value={value} onChange={onChange} options={field.options || []} {...common} />
        </Field>
      );

    case FIELD_TYPES.BOOLEAN:
      return (
        <div className="rms-field">
          <label className="rms-checkline">
            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
            {field.label}
          </label>
          {field.help ? <div className="rms-help">{field.help}</div> : null}
        </div>
      );

    case FIELD_TYPES.DATE:
      return (
        <Field label={field.label} required={field.required} help={field.help}>
          <input className="rms-input" type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...common} />
        </Field>
      );

    case FIELD_TYPES.CHECKLIST: {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="rms-field">
          <label className="rms-label">
            {field.label}
            {field.required ? <span className="rms-required">*</span> : null}
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2 }}>
            {(field.options || []).map((option) => {
              // Same shapes Select accepts, resolved the same way.
              const val = typeof option === "string" ? option : option.value;
              const lab = optionLabelFor(option);
              return (
                <label key={val} className="rms-checkline">
                  <input
                    type="checkbox"
                    checked={selected.includes(val)}
                    onChange={(e) =>
                      onChange(e.target.checked ? [...selected, val] : selected.filter((s) => s !== val))
                    }
                  />
                  {lab}
                </label>
              );
            })}
          </div>
          {field.help ? <div className="rms-help">{field.help}</div> : null}
        </div>
      );
    }

    default:
      return (
        <Field label={labelWithUnit(field)} required={field.required} help={field.help}>
          <TextInput value={value} onChange={onChange} {...common} />
        </Field>
      );
  }
}

function labelWithUnit(field) {
  return field.unit && field.unit !== "cents" ? `${field.label} (${field.unit})` : field.label;
}

// ----------------------------------------------------------------------------
// Signature capture
// ----------------------------------------------------------------------------

/**
 * Minimal pointer-driven signature pad. Returns a data URL on change.
 * Deliberately dependency-free — this runs at the counter on whatever device
 * the operator has, including cheap tablets.
 */
export function SignaturePad({ onChange, height = 130 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
  }, [height]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="rms-sigpad"
        style={{ height }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <span className="rms-help">{hasInk ? "Signature captured." : "Sign above with a finger, stylus or mouse."}</span>
        <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={clear}>
          {t("ui.clear", "Clear")}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Naming an asset
// ----------------------------------------------------------------------------
// A list that shows only the name forces staff to translate it into the code
// they actually work by, and they will translate it wrong on the day it
// matters. See utils/assetLabel.js for why the string form lives outside this
// file.

/**
 * Name on top, code beneath in monospace — for table cells, matching how the
 * inventory list already renders it. `extra` appends to the code line.
 */
export { assetLabel } from "../utils/assetLabel";

export function AssetCell({ asset, extra }) {
  if (!asset) return <span style={{ color: "var(--rms-muted)" }}>—</span>;
  return (
    <>
      <strong>{asset.name || "—"}</strong>
      <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
        {asset.asset_code ? (
          <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{asset.asset_code}</span>
        ) : null}
        {extra ? <>{asset.asset_code ? " · " : ""}{extra}</> : null}
      </div>
    </>
  );
}

/** Matches a bare calendar date with no time or zone, e.g. "2026-09-01". */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function formatDate(value, opts = {}) {
  if (!value) return "—";
  try {
    // Date-only strings (billing dates, certificate expiry) are calendar dates,
    // not instants. `new Date("2026-09-01")` parses as UTC midnight, which
    // renders as Aug 31 for anyone west of Greenwich — so a rent due date would
    // display a day early. Build these in local time instead.
    const date = DATE_ONLY.test(value)
      ? new Date(...value.split("-").map((n, i) => (i === 1 ? Number(n) - 1 : Number(n))))
      : new Date(value);

    return date.toLocaleDateString(getLocale(), { month: "short", day: "numeric", year: "numeric", ...opts });
  } catch {
    return String(value);
  }
}

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(getLocale(), { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return String(value);
  }
}
